import { AvailabilitySearchQuery, LockOptions } from 'types';
import { ExperienceCardAction } from 'utils/searchBar';
import { ExperienceResult } from 'components/www/availability-search/ExperienceResult';
import { FC } from 'react';
import { LocalDateTime } from 'js-joda';
import {
  SearchTicketGroupWithSeatingAreas,
  sortAndFilterOfferings,
} from 'components/www/availability-search/AvailabilitySearchResultSimple';
import { StoreType } from '@tock/shared/utils/connect';
import { consumer } from '@tock/coretypes';
import { oc } from 'ts-optchain';
import RestaurantResult from 'components/www/availability-search/RestaurantResult';
import _ from 'lodash';

export interface AvailabilitySearchResultProps {
  business: consumer.OfferingBusiness;
  lock: StoreType['lock'];
  locale: string;
  onLockTimeAndSize(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.OfferingBusiness,
    ticketTypeName?: string
  ): void;
  onNavigateToExperience(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    experienceListIdx: number
  ): void;
  onNavigateToExperienceMenu(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering
  ): void;
  timeZone: string;
  query: AvailabilitySearchQuery;
  hideExperiences: boolean;
  searchOfferings: consumer.SearchOffering[];
  experienceCardAction: ExperienceCardAction;
}

export const AvailabilitySearchResult: FC<AvailabilitySearchResultProps> = ({
  business,
  lock,
  locale,
  onLockTimeAndSize,
  onNavigateToExperience,
  onNavigateToExperienceMenu,
  timeZone,
  query,
  hideExperiences,
  searchOfferings,
  experienceCardAction,
}) => {
  const handleSelectDateAndTime = (
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    ticketTypeName?: string
  ): void => {
    onLockTimeAndSize(
      ticketTypeId,
      selectedSize,
      dateTime,
      priceCents,
      options,
      business,
      ticketTypeName
    );
  };

  // splitting results into ticketGroup and offering at this level helps remove ambiguity about undefined types in the ExperienceResult component
  const processedOfferings = sortAndFilterOfferings(
    searchOfferings,
    query.size,
    lock.currentLock,
    query.type
  );
  const mainProfileImage = _.first(_.map(oc(business).profileImage([]), (im) => im.imageUrl));

  return (
    <>
      <div className="AvailabilitySearch-summary">
        <RestaurantResult
          business={business}
          imageLink={mainProfileImage || business.heroImageUrl}
          showDescription={hideExperiences}
        />
      </div>

      {!hideExperiences && (
        <div className="AvailabilitySearch-reservations">
          <ul className="AvailabilitySearch-list">
            {_.map(processedOfferings, (processedOffering, idx) => {
              const offering = processedOffering.offering.offering as consumer.Offering;
              if (!processedOffering.offering?.offering?.id) {
                return null;
              }

              const { lockedDateTime, lockedSeatingArea } = processedOffering;

              return (
                <ExperienceResult
                  key={idx}
                  business={business}
                  locale={locale}
                  lockedDateTime={lockedDateTime ?? undefined}
                  lockedSeatingArea={lockedSeatingArea ?? undefined}
                  onLockTimeAndSize={handleSelectDateAndTime}
                  onNavigateToExperience={(
                    business: consumer.OfferingBusiness,
                    experience: consumer.Offering
                  ) => onNavigateToExperience(business, experience, idx)}
                  onNavigateToExperienceMenu={onNavigateToExperienceMenu}
                  offering={offering}
                  ticketGroup={
                    processedOffering.offering.ticketGroup as SearchTicketGroupWithSeatingAreas[]
                  }
                  timeZone={timeZone}
                  experienceCardAction={experienceCardAction}
                  query={query}
                />
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
};
